/**
 * InstallManager — PWA install experience manager.
 *
 * Handles:
 * - Capturing the beforeinstallprompt event
 * - Detecting iOS (Safari has no install prompt API)
 * - Detecting if app is already installed (standalone mode)
 * - Triggering native install prompt on Android/Desktop
 * - Providing iOS step-by-step instructions
 * - Persisting install state and suppression timers
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type InstallCallback = () => void;

const STORAGE_KEY_INSTALLED = 'aweni_pwa_installed';
const STORAGE_KEY_DISMISSED = 'aweni_install_dismissed';
const STORAGE_KEY_VISIT_COUNT = 'aweni_visit_count';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export class InstallManager {
  private _deferredPrompt: BeforeInstallPromptEvent | null = null;
  private _isInstalledFlag: boolean = false;
  private _installedCallbacks: InstallCallback[] = [];

  constructor() {
    // Detect if already running as installed PWA
    this._isInstalledFlag =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true ||
      localStorage.getItem(STORAGE_KEY_INSTALLED) === 'true';

    // Listen for beforeinstallprompt (Chrome, Edge, Samsung Internet)
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this._deferredPrompt = e as BeforeInstallPromptEvent;
    });

    // Listen for appinstalled event
    window.addEventListener('appinstalled', () => {
      this._isInstalledFlag = true;
      localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
      this._deferredPrompt = null;
      this._installedCallbacks.forEach(cb => cb());
    });

    // Track display-mode changes (user might install while page is open)
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      if (e.matches) {
        this._isInstalledFlag = true;
        localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
        this._installedCallbacks.forEach(cb => cb());
      }
    });

    // Increment visit count
    const count = parseInt(localStorage.getItem(STORAGE_KEY_VISIT_COUNT) || '0', 10);
    localStorage.setItem(STORAGE_KEY_VISIT_COUNT, String(count + 1));
  }

  /** True if the native install prompt is available (Android/Desktop Chrome). */
  isInstallable(): boolean {
    return this._deferredPrompt !== null;
  }

  /** True if the app is running as an installed PWA (standalone). */
  isInstalled(): boolean {
    return this._isInstalledFlag;
  }

  /** True if the device is iPhone or iPad (Safari). */
  isIOS(): boolean {
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  /** True if the user has visited at least N times. */
  hasVisitedAtLeast(n: number): boolean {
    const count = parseInt(localStorage.getItem(STORAGE_KEY_VISIT_COUNT) || '0', 10);
    return count >= n;
  }

  /** True if the user dismissed the banner and the cooldown hasn't expired. */
  isDismissed(): boolean {
    const ts = localStorage.getItem(STORAGE_KEY_DISMISSED);
    if (!ts) return false;
    const dismissedAt = parseInt(ts, 10);
    if (Date.now() - dismissedAt > DISMISS_DURATION_MS) {
      localStorage.removeItem(STORAGE_KEY_DISMISSED);
      return false;
    }
    return true;
  }

  /** Mark the install banner as dismissed for 7 days. */
  dismiss(): void {
    localStorage.setItem(STORAGE_KEY_DISMISSED, String(Date.now()));
  }

  /** Trigger the native browser install prompt. Returns true if accepted. */
  async promptInstall(): Promise<boolean> {
    if (!this._deferredPrompt) return false;
    try {
      await this._deferredPrompt.prompt();
      const { outcome } = await this._deferredPrompt.userChoice;
      this._deferredPrompt = null;
      if (outcome === 'accepted') {
        this._isInstalledFlag = true;
        localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
        return true;
      }
    } catch {
      // Prompt failed or was already used
    }
    return false;
  }

  /** Get iOS step-by-step instructions for adding to home screen. */
  getIOSInstructions(): Array<{ step: number; text: string; icon?: string }> {
    return [
      {
        step: 1,
        text: 'Tap the Share button at the bottom of Safari',
        icon: 'share',
      },
      {
        step: 2,
        text: 'Scroll down and tap "Add to Home Screen"',
        icon: 'plus-square',
      },
      {
        step: 3,
        text: 'Tap "Add" — AWENI is now on your home screen!',
        icon: 'check',
      },
    ];
  }

  /** Register a callback for when the app is installed. */
  onInstalled(callback: InstallCallback): void {
    this._installedCallbacks.push(callback);
  }

  /** Show a celebration toast after successful install. */
  showCelebration(): void {
    const toast = document.createElement('div');
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    toast.style.cssText = `
      position: fixed;
      top: 24px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10000;
      background: linear-gradient(135deg, #10B981, #059669);
      color: white;
      padding: 16px 28px;
      border-radius: 16px;
      font-size: 15px;
      font-weight: 600;
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4);
      animation: aweniSlideDown 400ms ease-out;
      max-width: 90vw;
      text-align: center;
    `;
    toast.textContent = 'AWENI installed! Find it on your home screen';

    // Add animation keyframe if not present
    if (!document.getElementById('aweni-install-animations')) {
      const style = document.createElement('style');
      style.id = 'aweni-install-animations';
      style.textContent = `
        @keyframes aweniSlideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes aweniSlideUp {
          from { opacity: 1; transform: translateX(-50%) translateY(0); }
          to { opacity: 0; transform: translateX(-50%) translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'aweniSlideUp 300ms ease-in forwards';
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }
}

/** Singleton instance */
let _instance: InstallManager | null = null;

export function getInstallManager(): InstallManager {
  if (!_instance) {
    _instance = new InstallManager();
  }
  return _instance;
}
